import React, { useState, useRef, useEffect } from "react";
const alarmSound = '/alarm.mp3';

const HIST_KEY = 'timer_history_v1';

export default function Timer() {
  const [minutes, setMinutes] = useState(1);
  const [seconds, setSeconds] = useState(0);
  const [totalSeconds, setTotalSeconds] = useState(60);
  const [temperature, setTemperature] = useState('90');
  const [running, setRunning] = useState(false);
  const [timerHistory, setTimerHistory] = useState([]);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'warning' });
  const timerRef = useRef(null);
  const alarmRef = useRef(null);
  const initialDataRef = useRef({ min: 1, seg: 0, temp: 90 });

  const handleMinutesChange = (e) => {
    const value = e.target.value;
    if (value === '') {
      setMinutes('');
      setTotalSeconds(seconds || 0);
      return;
    }
    const min = Math.max(0, Math.min(60, Number(value)));
    setMinutes(min);
    setTotalSeconds(min * 60 + (isNaN(seconds) ? 0 : seconds));
  };

  const handleSecondsChange = (e) => {
    const value = e.target.value;
    if (value === '') {
      setSeconds('');
      setTotalSeconds((isNaN(minutes) ? 0 : minutes) * 60);
      return;
    }
    const sec = Math.max(0, Math.min(59, Number(value)));
    setSeconds(sec);
    setTotalSeconds((isNaN(minutes) ? 0 : minutes) * 60 + sec);
  };

  const handleTemperatureChange = (e) => {
    const value = e.target.value;
    if (value === '') {
      setTemperature('');
      return;
    }
    const temp = Number(value);
    if (!isNaN(temp)) {
      setTemperature(value); // Guardar como string para permitir campo vacío
    }
  };

  const startTimer = () => {
    // Validar condiciones de alerta
    const mins = isNaN(minutes) ? 0 : minutes;
    const secs = isNaN(seconds) ? 0 : seconds;
    const temp = temperature === '' ? 0 : Number(temperature);
    const totalTimeInSeconds = (mins * 60) + secs;
    
    // Verificar condiciones de alerta (solo mostrar advertencia, no bloquear)
    if (temp > 110 && totalTimeInSeconds > 30) {
      setAlert({
        show: true,
        message: '¡Alta temperatura! No se recomienda más de 110°C por más de 30 segundos.',
        type: 'error'
      });
    } else if (temp > 95 && totalTimeInSeconds > 60) {
      setAlert({
        show: true,
        message: 'Tiempo prolongado. No se recomienda más de 1 minuto para temperaturas sobre 95°C.',
        type: 'warning'
      });
    } else {
      setAlert({ show: false, message: '', type: 'warning' });
    }
    
    initialDataRef.current = { min: mins, seg: secs, temp: temp };
    setRunning(true);
    setTotalSeconds(mins * 60 + secs);
    timerRef.current = setInterval(() => {
      setTotalSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setRunning(false);
          if (alarmRef.current) {
            try {
              alarmRef.current.currentTime = 0;
              alarmRef.current.play();
            } catch (error) {
              console.error('Error playing alarm:', error);
            }
          }
          if (initialDataRef.current.min > 0 || initialDataRef.current.seg > 0) {
            const now = new Date();
            const newEntry = {
              min: initialDataRef.current.min,
              seg: initialDataRef.current.seg,
              temp: initialDataRef.current.temp,
              fecha: now.toLocaleString(),
            };
            setTimerHistory(h => {
              if (h.length > 0 && h[0].min === newEntry.min && h[0].seg === newEntry.seg && h[0].temp === newEntry.temp && h[0].fecha === newEntry.fecha) {
                return h;
              }
              const updated = [newEntry, ...h];
              return updated.length > 10 ? updated.slice(0, 10) : updated;
            });
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    if (totalSeconds === 0) {
      setMinutes(0);
      setSeconds(0);
    } else if (running) {
      setMinutes(Math.floor(totalSeconds / 60));
      setSeconds(totalSeconds % 60);
    }
  }, [totalSeconds, running]);

  const stopTimer = () => {
    clearInterval(timerRef.current);
    setRunning(false);
  };

  const resetTimer = () => {
    clearInterval(timerRef.current);
    const mins = initialDataRef.current.min || 0;
    const secs = initialDataRef.current.seg || 0;
    setMinutes(mins);
    setSeconds(secs);
    setTotalSeconds(mins * 60 + secs);
    if (running) {
      setRunning(false);
      if (alarmRef.current) {
        alarmRef.current.pause();
        alarmRef.current.currentTime = 0;
      }
    }
  };

  useEffect(() => {
    const h = localStorage.getItem(HIST_KEY);
    if (h) setTimerHistory(JSON.parse(h));
  }, []);

  useEffect(() => {
    localStorage.setItem(HIST_KEY, JSON.stringify(timerHistory));
  }, [timerHistory]);

  return (
    <div style={{
      background: '#000000',
      minHeight: '100vh',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '24px'
    }}>
      <audio ref={alarmRef} src={alarmSound} preload="auto" />
      <div style={{
        width: '100%',
        maxWidth: 800,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 24
      }}>
        <h1 style={{color:'#fff',fontSize:'2.4em',fontWeight:800,letterSpacing:'.01em'}}>Cronómetro</h1>
        
        {/* Alertas */}
        {alert.show && (
          <div style={{
            width: '100%',
            padding: '12px 20px',
            borderRadius: '8px',
            backgroundColor: alert.type === 'error' ? '#ffebee' : '#fff8e1',
            color: alert.type === 'error' ? '#c62828' : '#e65100',
            border: `1px solid ${alert.type === 'error' ? '#ef9a9a' : '#ffe082'}`,
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            maxWidth: '500px',
            boxSizing: 'border-box'
          }}>
            <span style={{
              fontSize: '1.2em',
              fontWeight: 'bold'
            }}>
              {alert.type === 'error' ? '⚠️' : 'ℹ️'}
            </span>
            <span style={{ flex: 1 }}>{alert.message}</span>
            <button 
              onClick={() => setAlert({ ...alert, show: false })}
              style={{
                background: 'none',
                border: 'none',
                color: 'inherit',
                cursor: 'pointer',
                fontSize: '1.2em',
                padding: '0 5px'
              }}
            >
              ✕
            </button>
          </div>
        )}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16
        }}>
          <input
            type="number"
            value={minutes === 0 && !running ? '' : minutes}
            onChange={handleMinutesChange}
            style={{
              width: 60,
              padding: '8px',
              fontSize: '1.2em',
              border: 'none',
              borderRadius: 8,
              backgroundColor: '#2d2d2d',
              color: '#fff',
              textAlign: 'center'
            }}
            min="0"
            max="99"
          />
          <span style={{color:'#fff',fontSize:'1.2em'}}>:</span>
          <input
            type="number"
            value={seconds === 0 && !running ? '' : seconds}
            onChange={handleSecondsChange}
            style={{
              width: 60,
              padding: '8px',
              fontSize: '1.2em',
              border: 'none',
              borderRadius: 8,
              backgroundColor: '#2d2d2d',
              color: '#fff',
              textAlign: 'center'
            }}
            min="0"
            max="59"
          />
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16
        }}>
          <input
            type="number"
            value={temperature}
            onChange={handleTemperatureChange}
            onBlur={(e) => {
              if (e.target.value === '') {
                setTemperature('90');
              } else {
                const value = Math.max(0, Math.min(120, Number(e.target.value)));
                setTemperature(value.toString());
              }
            }}
            style={{
              width: 80,
              padding: '8px',
              fontSize: '1.2em',
              border: 'none',
              borderRadius: 8,
              backgroundColor: '#2d2d2d',
              color: '#fff',
              textAlign: 'center'
            }}
            min="0"
            max="120"
          />
          <span style={{color:'#fff',fontSize:'1.2em'}}>°C</span>
        </div>
        <div style={{
          display: 'flex',
          gap: 16,
          marginTop: 24
        }}>
          <button
            onClick={startTimer}
            style={{
              padding: '12px 24px',
              background: running ? '#666' : '#4caf50',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: '1.1em',
              cursor: 'pointer',
              transition: 'background 0.2s'
            }}
            disabled={running}
          >
            Iniciar
          </button>
          <button
            onClick={stopTimer}
            style={{
              padding: '12px 24px',
              background: '#f44336',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: '1.1em',
              cursor: 'pointer',
              transition: 'background 0.2s'
            }}
            disabled={!running}
          >
            Detener
          </button>
          <button
            onClick={resetTimer}
            style={{
              padding: '12px 24px',
              background: '#2196f3',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: '1.1em',
              cursor: 'pointer',
              transition: 'background 0.2s'
            }}
          >
            Reiniciar
          </button>
        </div>
        <div style={{
          marginTop: 24,
          width: '100%',
          maxWidth: 600,
          backgroundColor: '#2d2d2d',
          borderRadius: 12,
          padding: '16px',
          maxHeight: '400px',
          overflowY: 'auto'
        }}>
          <h2 style={{
            color: '#fff',
            fontSize: '1.4em',
            marginBottom: 16
          }}>Historial</h2>
          {timerHistory.length === 0 ? (
            <div style={{color:'#ccc', fontSize:'0.98em'}}>No hay registros aún.</div>
          ) : (
            <div style={{display:'flex', flexDirection:'column', gap:7}}>
              {timerHistory.map((entry, index) => (
                <div key={index} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 0',
                  borderBottom: '1px solid #444',
                  color: '#fff'
                }}>
                  <div>
                    <span style={{fontSize: '1.1em'}}>{entry.min} min {entry.seg} seg</span>
                    <br />
                    <span style={{fontSize: '0.9em', color: '#888'}}>{entry.fecha}</span>
                  </div>
                  <span style={{
                    fontSize: '1.1em',
                    color: entry.temp >= 90 ? '#4caf50' : '#f44336'
                  }}>{entry.temp}°C</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
